import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  getJobProfileSettings,
  saveJobProfileSettings,
} from '@/api/jobProfileSettings';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import {
  canSaveSettings,
  getLevelNameErrors,
  normalizeFieldRows,
  normalizeLevelRows,
  withOrderIndex,
} from '@/helpers/jobProfileSettingsValidation';
import { useTracking } from '@/stores/tracking/tracking';

export const useJobProfileSettings = defineStore('jobProfileSettings', () => {
  const { setLoading } = useAppLoading();
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

  const fields = ref([]);
  const levels = ref([]);
  const isDirty = ref(false);

  let newLevelCount = 0;

  const levelErrors = computed(() => getLevelNameErrors(levels.value));

  const isSaveValid = computed(() => canSaveSettings({
    fields: fields.value,
    levels: levels.value,
  }));

  const syncSortOrders = () => {
    fields.value = withOrderIndex(normalizeFieldRows(fields.value));
    levels.value = withOrderIndex(normalizeLevelRows(levels.value));
    isDirty.value = true;
  };

  const hydrateFromPayload = (payload) => {
    const nextFields = normalizeFieldRows(payload.fields || []);
    const nextLevels = normalizeLevelRows(
      (payload.levels || []).map((row) => ({ ...row, rowKey: row.ulid })),
    );

    fields.value = withOrderIndex(nextFields);
    levels.value = withOrderIndex(nextLevels);

    isDirty.value = false;
  };

  const load = async () => {
    try {
      setLoading({ pageLoading: true });

      const data = await getJobProfileSettings();

      hydrateFromPayload(data);
    } catch (err) {
      const message = err?.message || String(err);

      addErrorSnackbar({ text: `Job profile settings - ${message}` });

      throw err;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const save = async () => {
    if (!isSaveValid.value) return;

    syncSortOrders();

    try {
      setLoading({ componentLoading: true });

      const data = await saveJobProfileSettings({
        fields: fields.value,
        levels: levels.value.map((row) => ({ ...row, name: row.name.trim() })),
      });

      useTracking().jobProfileSettingsUpdated({
        section: 'Job Profile Settings',
        field: 'Update',
      });

      hydrateFromPayload(data);
      addSuccessSnackbar({ text: 'Job profile settings have been updated' });
    } catch (err) {
      const message = err?.message || String(err);

      addErrorSnackbar({ text: `Job profile settings - ${message}` });

      throw err;
    } finally {
      setLoading({ componentLoading: false });
    }
  };

  const setFieldVisible = (fieldUlid, visible) => {
    fields.value = fields.value.map((row) => {
      if (row.ulid !== fieldUlid) return row;

      const nextHidden = !visible;
      const nextRequired = nextHidden ? false : row.is_required;

      return { ...row, is_hidden: nextHidden, is_required: nextRequired };
    });

    isDirty.value = true;
    syncSortOrders();
  };

  const setFieldRequired = (fieldUlid, required) => {
    fields.value = fields.value.map((row) => {
      if (row.ulid !== fieldUlid) return row;

      return {
        ...row,
        is_required: Boolean(required) && !row.is_hidden,
      };
    });
    isDirty.value = true;
    syncSortOrders();
  };

  const setLevelAlias = (rowKey, shortName) => {
    levels.value = levels.value.map((row) => (
      row.rowKey === rowKey ? { ...row, short_name: String(shortName ?? '') } : row
    ));

    isDirty.value = true;
    syncSortOrders();
  };

  const setLevelName = (rowKey, name) => {
    levels.value = levels.value.map((row) => (
      row.rowKey === rowKey ? { ...row, name: String(name ?? '') } : row
    ));

    isDirty.value = true;
  };

  const addLevel = () => {
    newLevelCount += 1;

    levels.value = withOrderIndex([
      ...levels.value,
      {
        ulid: null,
        rowKey: `new-level-${newLevelCount}`,
        name: '',
        short_name: '',
      },
    ]);

    isDirty.value = true;
  };

  const reset = () => {
    fields.value = [];
    levels.value = [];
    isDirty.value = false;
    newLevelCount = 0;
  };

  return {
    fields,
    levels,
    isDirty,
    levelErrors,
    isSaveValid,
    load,
    save,
    syncSortOrders,
    setFieldVisible,
    setFieldRequired,
    setLevelAlias,
    setLevelName,
    addLevel,
    reset,
    hydrateFromPayload,
  };
});
